import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { sprintf } from 'sprintf-js';

import { environment } from '../../environments/environment';
import { BusinessRule } from '../_models';
import { BaseService } from './base.service';
import { BUSINESS_RULE_NAMES } from 'app/app.constants';

@Injectable({
  providedIn: 'root',
})
export class BusinessRuleService extends BaseService {
  constructor(protected override http: HttpClient) {
    super(http, `${environment.apiBaseUrl}/companies/%(company_id)s/business_rules`);
  }

  getRule(
    fiscalYearId: string,
    name: string,
    urlParams: { company_id: string },
  ): Observable<BusinessRule | null> {
    const url = sprintf(this.baseUrl, urlParams);
    const params: { fiscal_year_id?: string } = {};
    if (fiscalYearId) params.fiscal_year_id = fiscalYearId;
    return this.http.get<BusinessRule | null>(`${url}/${encodeURIComponent(name)}`, {
      params: this.toHttpParams(params),
    });
  }

  upsertRule(
    fiscalYearId: string,
    name: string,
    ruleValue: Record<string, unknown>,
    urlParams: { company_id: string },
  ): Observable<BusinessRule> {
    const url = sprintf(this.baseUrl, urlParams);
    const params: { fiscal_year_id?: string } = {};
    if (fiscalYearId) params.fiscal_year_id = fiscalYearId;
    return this.http.put<BusinessRule>(`${url}/${encodeURIComponent(name)}`, ruleValue, {
      params: this.toHttpParams(params),
    });
  }

  autoCancelViolations(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<unknown[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<unknown[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.AUTO_CANCEL)}/violations`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  eventSpeakerViolations(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.EVENT_SPEAKER)}/violations`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  eventTopicViolations(
    fiscalYearId: string,
    year: number | undefined,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    const params: Record<string, string> = { fiscal_year_id: fiscalYearId };
    if (year != null && Number.isFinite(year)) {
      params.year = String(year);
    }
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.EVENT_TOPIC)}/violations`, {
      params: this.toHttpParams(params),
    });
  }

  getEventsWithoutHcpMin(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.ATTENDANCE_REQUIREMENT)}/events_without_hcp_min`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  /** HCP Defined: attendee rows needing credential / license / specialty fixes (per fiscal year). */
  hcpDefinedAttendeeViolations(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/hcp_defined/attendee_violations`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  /** Attendee Rules: repeat-attendance violations (same-topic / same-version, calendar-year / rolling-12-months). */
  attendeeRulesRepeatAttendanceViolations(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/attendee_rules/repeat_attendance_violations`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  /** HCP Defined: same logic scoped to one event (audience review tab). */
  hcpDefinedEventAttendeeReview(
    eventId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/hcp_defined/event_attendee_review`, {
      params: this.toHttpParams({ event_id: eventId }),
    });
  }

  getBelowMinHcpEvents(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.ATTENDANCE_REQUIREMENT)}/below_min_hcp_events`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  /** Three noncompliance counts for the Rules Overview attendance badges. */
  getAttendanceComplianceCounts(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<{ minAttendeesCount: number; minHcpCount: number; multiOfficeCount: number }> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<{ minAttendeesCount: number; minHcpCount: number; multiOfficeCount: number }>(
      `${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.ATTENDANCE_REQUIREMENT)}/violation_counts`,
      { params: this.toHttpParams({ fiscal_year_id: fiscalYearId }) },
    );
  }

  getPostProgramPaperworkPendingEvents(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.POST_PROGRAM_PAPERWORK)}/post_program_paperwork_pending_events`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  speakerRequirementViolations(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.SPEAKER_REQUIREMENT)}/speaker_requirement_violations`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  getSignInSheetPendingEvents(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.SIGN_IN_SHEET)}/sign_in_sheet_pending_events`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  mealCostViolations(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.MEAL_COST)}/violations`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  attendeeRestrictionsRulesViolations(
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.ATTENDEE_RESTRICTIONS_RULES)}/violations`);
  }

  programRequestRulesViolations(
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/program_request_rules/violations`);
  }

  contractEndDateViolations(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.CONTRACT_END_DATE)}/violations`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  getReconciledDueDatePendingEvents(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.RECONCILED_DUE_DATE)}/reconciled_due_date_pending_events`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  getSameDaySchedulingConflicts(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.SAME_DAY_SCHEDULING)}/same_day_scheduling_conflicts`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  /**
   * Contractual Rates live report. Returns { speakers, events }:
   *  - speakers: contracts effective in [startDate, endDate] whose rate sheet is off-rate.
   *  - events: events in that window with a fee-for-service payment that matches no aligned contract rate.
   */
  contractualRatesReport(
    startDate: string,
    endDate: string,
    hourlyRateRequired: boolean,
    urlParams: { company_id: string },
  ): Observable<{ speakers: any[]; events: any[] }> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<{ speakers: any[]; events: any[] }>(
      `${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.CONTRACTUAL_RATES)}/contractual_rates_report`,
      {
        params: this.toHttpParams({
          start_date: startDate,
          end_date: endDate,
          hourly_rate_required: hourlyRateRequired ? 'true' : 'false',
        }),
      },
    );
  }

  travelRestrictionViolations(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<any[]> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<any[]>(`${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.TRAVEL_RESTRICTIONS)}/travel_violations`, {
      params: this.toHttpParams({ fiscal_year_id: fiscalYearId }),
    });
  }

  /** Four per-rule counts for the Rules Overview travel badges. */
  travelRestrictionViolationCounts(
    fiscalYearId: string,
    urlParams: { company_id: string },
  ): Observable<{
    ground: { past: number; future: number };
    air: { past: number; future: number };
    hotel: { past: number; future: number };
    speaker_reimbursement: { past: number; future: number };
  }> {
    const url = sprintf(this.baseUrl, urlParams);
    return this.http.get<{
      ground: { past: number; future: number };
      air: { past: number; future: number };
      hotel: { past: number; future: number };
      speaker_reimbursement: { past: number; future: number };
    }>(
      `${url}/${encodeURIComponent(BUSINESS_RULE_NAMES.TRAVEL_RESTRICTIONS)}/travel_violation_counts`,
      { params: this.toHttpParams({ fiscal_year_id: fiscalYearId }) },
    );
  }
}
